import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useLocale } from '../contexts/LocaleContext';
import { createTranslator } from '../i18n';
import { useTypography } from '../hooks/useTypography';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface ReplyIndicatorProps {
  message: {
    messageId: string;
    content: string;
    type: string;
    direction: 'inbound' | 'outbound' | 'outbound-api';
    contactName?: string;
    contactPhone?: string;
    from?: string;
  };
  onCancel?: () => void;
  showCancelButton?: boolean;
  onPress?: () => void;
  isOutgoing?: boolean; // Add this to adapt styling based on message direction
  fallbackContactName?: string;
}

export const ReplyIndicator: React.FC<ReplyIndicatorProps> = ({
  message,
  onCancel,
  showCancelButton = false,
  onPress,
  isOutgoing = false,
  fallbackContactName,
}) => {
  const styles = useThemedStyles(getStyles);
  const { isRTL, locale } = useLocale();
  const t = createTranslator(locale);
  const typo = useTypography();
  const rtl = useRTLStyles();

  const looksLikePhoneNumber = (value?: string) => {
    if (!value) {
      return false;
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return false;
    }
    return /^[+]?[-()\s\d]{6,}$/.test(trimmed);
  };

  const resolveContactLabel = (...candidates: Array<string | undefined>) => {
    for (const candidate of candidates) {
      if (!candidate) {
        continue;
      }
      const trimmed = candidate.trim();
      if (!trimmed || trimmed.toLowerCase() === 'unknown contact') {
        continue;
      }
      if (looksLikePhoneNumber(trimmed)) {
        continue;
      }
      return trimmed;
    }
    if (fallbackContactName && !looksLikePhoneNumber(fallbackContactName)) {
      return fallbackContactName;
    }
    return undefined;
  };

  const getDisplayName = () => {
    if (message.direction === 'outbound' || message.direction === 'outbound-api') {
      return t('chat.you');
    }

    const resolvedLabel = resolveContactLabel(
      message.contactName,
      message.from && message.from !== 'business' && message.from !== 'system' ? message.from : undefined
    );

    return resolvedLabel || fallbackContactName || t('chat.contact');
  };

  const parseTemplatePayload = () => {
    if (message.type !== 'template') {
      return null;
    }

    if (typeof message.content === 'string') {
      try {
        return JSON.parse(message.content);
      } catch (error) {
        // Ignore parse errors
      }
    }

    if (message.content && typeof message.content === 'object') {
      return message.content;
    }

    return null;
  };

  const extractTemplateBody = (payload: any): string | undefined => {
    if (!payload) {
      return undefined;
    }

    if (typeof payload?.body?.text === 'string') {
      return payload.body.text;
    }

    if (typeof payload?.body === 'string') {
      return payload.body;
    }

    if (Array.isArray(payload?.components)) {
      const bodyComponent = payload.components.find((component: any) => (component?.type || '').toUpperCase() === 'BODY');
      if (bodyComponent) {
        if (typeof bodyComponent.text === 'string') {
          return bodyComponent.text;
        }
        if (Array.isArray(bodyComponent.parameters)) {
          return bodyComponent.parameters
            .map((param: any) => {
              if (typeof param?.text === 'string') {
                return param.text;
              }
              if (typeof param?.title === 'string') {
                return param.title;
              }
              if (typeof param?.payload === 'string') {
                return param.payload;
              }
              if (typeof param === 'string') {
                return param;
              }
              return '';
            })
            .filter(Boolean)
            .join(' ')
            .trim();
        }
      }
    }

    return undefined;
  };

  const getTemplatePreviewText = () => {
    const payload = parseTemplatePayload();
    if (!payload) {
      return undefined;
    }
    const body = extractTemplateBody(payload);
    if (body && body.length > 0) {
      return body.replace(/\*(.*?)\*/g, '$1');
    }
    return t('chat.templateMessage');
  };

  /** Extract display text from content (string or JSON/object-like string). */
  const getDisplayContent = (content: any): string => {
    if (content == null) return '';
    if (typeof content === 'string') {
      const trimmed = content.trim();
      if (trimmed.startsWith('{')) {
        try {
          const obj = JSON.parse(trimmed);
          const text =
            (typeof obj.body === 'string' && obj.body.trim()) ? obj.body.trim() :
            (obj.body && typeof obj.body.text === 'string' && obj.body.text.trim()) ? obj.body.text.trim() :
            (typeof obj.text === 'string' && obj.text.trim()) ? obj.text.trim() :
            (typeof obj.title === 'string' && obj.title.trim()) ? obj.title.trim() :
            '';
          if (text) return text;
        } catch (_) {}
        // Fallback for object-like strings (e.g. {text:"..."} or {"text":"..."})
        const textMatch = trimmed.match(/(?:^|[,{])\s*"?text"?\s*:\s*"((?:[^"\\]|\\.)*)"|(?:^|[,{])\s*"?text"?\s*:\s*'([^']*)'/);
        if (textMatch) {
          const v = (textMatch[1] ?? textMatch[2] ?? '').trim();
          if (v) return v;
        }
        const bodyMatch = trimmed.match(/(?:^|[,{])\s*"?body"?\s*:\s*"((?:[^"\\]|\\.)*)"|(?:^|[,{])\s*"?body"?\s*:\s*'([^']*)'/);
        if (bodyMatch) {
          const v = (bodyMatch[1] ?? bodyMatch[2] ?? '').trim();
          if (v) return v;
        }
      }
      return trimmed;
    }
    if (typeof content === 'object') {
      const body = content.body;
      if (typeof body === 'string' && body.trim()) return body.trim();
      if (body && typeof body.text === 'string' && body.text.trim()) return body.text.trim();
      if (typeof content.text === 'string' && content.text.trim()) return content.text.trim();
      if (typeof content.title === 'string' && content.title.trim()) return content.title.trim();
    }
    return '';
  };

  const getContentDisplay = () => {
    switch (message.type) {
      case 'text':
        return getDisplayContent(message.content) || message.content;
      case 'template':
        return getTemplatePreviewText();
      case 'image':
        return `📷 ${t('chat.image')}`;
      case 'video':
        return `🎥 ${t('chat.video')}`;
      case 'audio':
        return `🎵 ${t('chat.audio')}`;
      case 'document':
        return `📄 ${t('chat.document')}`;
      case 'location':
        return `📍 ${t('chat.location')}`;
      case 'contact':
        return `👤 ${t('chat.contact')}`;
      case 'sticker':
        return `😀 ${t('chat.sticker')}`;
      case 'interactive':
        return getDisplayContent(message.content) || t('chat.interactiveMessage');
      default:
        return getDisplayContent(message.content) || t('chat.message');
    }
  };

  return (
    <TouchableOpacity
      style={[styles.replyIndicator, { flexDirection: rtl.rowDirection }, isRTL && { borderLeftWidth: 0, borderRightWidth: 4, borderRightColor: colors.primary }]}
      onPress={onPress}
      activeOpacity={onPress ? 0.7 : 1}
      disabled={!onPress}
    >
      <View style={[styles.replyIconContainer, rtl.marginStart(spacing.sm)]}>
        <Ionicons name="arrow-undo" size={18} color={colors.white} style={isRTL ? { transform: [{ scaleX: -1 }] } : undefined} />
      </View>
      <View style={styles.replyContentContainer}>
        <View style={[styles.replyHeader, { flexDirection: rtl.rowDirection }]}>
          <Text style={[styles.replyLabel, { textAlign: rtl.textStart }]}>
            {t('chat.replyingTo', { name: getDisplayName() })}
          </Text>
          {showCancelButton && onCancel && (
            <TouchableOpacity onPress={onCancel} style={styles.replyCancelButton}>
              <Ionicons name="close" size={18} color={colors.text.secondary} />
            </TouchableOpacity>
          )}
        </View>
        <Text style={[styles.replyContent, { textAlign: rtl.textStart }]} numberOfLines={2}>
          {getContentDisplay()}
        </Text>
      </View>
    </TouchableOpacity>
  );
};

const getStyles = () => StyleSheet.create({
  replyIndicator: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: 'rgba(20, 99, 165, 0.08)',
    borderRadius: 10,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
    borderWidth: 1,
    borderColor: 'rgba(20, 99, 165, 0.15)',
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
    minWidth: 240,
  },
  replyIconContainer: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  replyContentContainer: {
    flex: 1,
  },
  replyHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs / 2,
  },
  replyLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.primary,
    flex: 1,
  },
  replyCancelButton: {
    padding: spacing.xs / 2,
    borderRadius: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
  },
  replyContent: {
    fontSize: 13,
    color: colors.text.primary,
    lineHeight: 18,
    fontFamily: typography.fontFamily.regular,
  },
});
